import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook personnalisé pour gérer les ResizeObserver de manière sécurisée
 * Implémente les meilleures pratiques pour éviter les loops ResizeObserver
 */
export const useResizeObserverFix = () => {
  const frameRef = useRef();
  const stateUpdatesRef = useRef([]);
  const observersRef = useRef(new Set());

  // Batching des mises à jour d'état avec requestAnimationFrame
  const batchStateUpdate = useCallback((updateFn) => {
    stateUpdatesRef.current.push(updateFn);

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      // Exécuter toutes les mises à jour en une fois
      const updates = stateUpdatesRef.current.splice(0);
      updates.forEach(update => {
        try {
          update();
        } catch (error) {
          // Ignorer silencieusement les erreurs ResizeObserver
          if (!error.message?.includes('ResizeObserver')) {
            console.error('State update error:', error);
          }
        }
      });
      frameRef.current = null;
    });
  }, []);

  // Création sécurisée d'un ResizeObserver
  const createSafeResizeObserver = useCallback((callback) => {
    if (!window.ResizeObserver) return null;

    const safeCallback = (entries, observer) => {
      // Utiliser requestAnimationFrame pour différer le traitement
      batchStateUpdate(() => {
        try {
          callback(entries, observer);
        } catch (error) {
          if (!error.message?.includes('ResizeObserver')) {
            console.error('ResizeObserver callback error:', error);
          }
        }
      });
    };

    const observer = new ResizeObserver(safeCallback);
    observersRef.current.add(observer);
    return observer;
  }, [batchStateUpdate]);

  // Cleanup automatique des observers
  const cleanupObserver = useCallback((observer) => {
    if (observer) {
      try {
        observer.disconnect();
        observersRef.current.delete(observer);
      } catch (error) {
        // Ignorer les erreurs de cleanup
      }
    }
  }, []);

  // Cleanup au démontage du composant
  useEffect(() => {
    return () => {
      // Annuler les frames en attente
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      // Nettoyer tous les observers
      observersRef.current.forEach(observer => {
        try {
          observer.disconnect();
        } catch (error) {
          // Ignorer les erreurs de cleanup
        }
      });
      observersRef.current.clear();

      // Vider les mises à jour en attente
      stateUpdatesRef.current = [];
    };
  }, []);

  return {
    batchStateUpdate,
    createSafeResizeObserver,
    cleanupObserver
  };
};

/**
 * Hook pour gérer les changements de Select de manière sécurisée
 */
export const useSafeSelectHandler = (onValueChange) => {
  const { batchStateUpdate } = useResizeObserverFix();
  const timeoutRef = useRef();

  const handleSelectChange = useCallback((value) => {
    // Débounce pour éviter les changements rapides
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Batching de la mise à jour avec requestAnimationFrame
      batchStateUpdate(() => {
        if (onValueChange) {
          onValueChange(value);
        }
      });
    }, 16); // ~1 frame à 60fps
  }, [onValueChange, batchStateUpdate]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return handleSelectChange;
};

/**
 * Hook pour gérer les mutations DOM de manière sécurisée
 */
export const useSafeDOMUpdate = () => {
  const { batchStateUpdate } = useResizeObserverFix();

  const safeDOMUpdate = useCallback((domUpdateFn) => {
    // Différer les mutations DOM au prochain frame
    batchStateUpdate(() => {
      // Attendre un tick supplémentaire pour que les changements de layout se stabilisent
      requestAnimationFrame(() => {
        try {
          domUpdateFn();
        } catch (error) {
          if (!error.message?.includes('ResizeObserver')) {
            console.error('DOM update error:', error);
          }
        }
      });
    });
  }, [batchStateUpdate]);

  return safeDOMUpdate;
};

export default useResizeObserverFix;